import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase';

class SectoralSidebarService {
  constructor() {
    this.cache = {};
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
  }

  // Cache key generator
  getCacheKey(sector) {
    return `sectoral_sidebar_${sector}`;
  }

  // Check if cache is valid
  isCacheValid(cacheKey) {
    const cached = this.cache[cacheKey];
    if (!cached) return false;

    const now = Date.now();
    return (now - cached.timestamp) < this.cacheExpiry;
  }

  // Get sectoral sidebar configuration with smart caching
  async getSectoralSidebar(sector) {
    const cacheKey = `sectoral_sidebar_cache_${sector}`;
    
    // 🛡️ [GUARANTEED CACHE BUSTER] Force clear all local menu caches
    const CURRENT_FORCE_VERSION = '20261007_v21_whatsapp_sidebar';
    const lastForceVersion = localStorage.getItem('sidebar_force_version');
    if (lastForceVersion !== CURRENT_FORCE_VERSION) {
      console.log('🚀 [PR MANAGER] System update detected! Refreshing menu structure...');
      // Clear all possible sidebar-related keys
      Object.keys(localStorage).forEach(key => {
        if (key.includes('sidebar') || key.includes('menu') || key.includes('sectoral')) {
          localStorage.removeItem(key);
        }
      });
      localStorage.setItem('sidebar_force_version', CURRENT_FORCE_VERSION);
      // Clean instance cache too
      this.cache = {};
    }

    const cachedData = localStorage.getItem(cacheKey);
    let localCache = null;
    
    try {
      if (cachedData) {
        localCache = JSON.parse(cachedData);
      }
    } catch (e) {
      console.warn('Sidebar cache parse error:', e);
    }

    // 🛡️ AUTH CHECK: Yetkisiz Firestore isteği atılmasını önle
    if (!auth?.currentUser) {
      console.debug(`sectoralSidebarService: Auth henüz hazır değil, cache veya varsayılan döndürülüyor.`);
      if (localCache) return localCache.items;
      return this.getDefaultSectoralSidebar(sector);
    }
    
    try {
      console.log(`🔄 Loading sectoral sidebar for sector: ${sector}`);
      const configType = `sectoral_${sector}`;
      const docRef = doc(db, 'sidebarConfigs', configType);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const remoteTimestamp = data.lastUpdated || data.updated_at || 'initial';
        
        // 🚀 SMART CACHE CHECK: If timestamp hasn't changed, return local cache
        if (localCache && localCache.lastUpdated === remoteTimestamp) {
          console.log(`⚡ Using localStorage cache for ${sector} (Timestamp: ${remoteTimestamp})`);
          return localCache.items;
        }

        console.log(`📥 Cache miss or update for ${sector}. Loading from Firestore...`);
        
        // NEW data first, then fallback to old
        let sectoralConfig = data.menuConfig || data.menuItems || this.getDefaultSectoralSidebar(sector);

        // 🏨 Hospitality Patch (Ensure critical links exist)
        if (sector === 'hospitality' || sector === 'service_hospitality' || sector === 'service') {
          const settingsGroup = sectoralConfig.find(item => item.id === 'hotel_settings_group' || item.id === 'settings_group');
          if (settingsGroup && !settingsGroup.children.some(child => child.id === 'website-generator-settings')) {
            settingsGroup.children.unshift({
              id: 'website-generator-settings',
              label: 'Web & QR Yönetimi',
              path: '/sectors/service/hospitality/settings/website',
              visible: true,
              icon: 'QrCodeIcon'
            });
          }
        }

        // SAVE to localStorage cache
        localStorage.setItem(cacheKey, JSON.stringify({
          lastUpdated: remoteTimestamp,
          items: sectoralConfig
        }));

        return sectoralConfig;
      } else {
        // --- FALLBACK KALDIRILDI ---
        // Her zaman yeni canonical yapıyı kullan.
        return this.getDefaultSectoralSidebar(sector);
      }
    } catch (error) {
      console.error(`❌ Error loading ${sector}:`, error);
      // Fallback to local cache if error occurs
      if (localCache) return localCache.items;
      return this.getDefaultSectoralSidebar(sector);
    }
  }

  // Helper for legacy restaurant sidebar fallback
  async getLegacyRestaurantSidebar() {
    try {
      // 🎯 v4.0.0: Önce standart admin_settings dener, yoksa systemConfig fallback yapar
      let menuSnap = await getDoc(doc(db, 'admin_settings', 'menu_configuration'));
      if (!menuSnap.exists()) {
        menuSnap = await getDoc(doc(db, 'systemConfig', 'menuConfiguration'));
      }
      if (menuSnap.exists() && menuSnap.data().businessSidebar) {
        return menuSnap.data().businessSidebar;
      }

      const oldBusinessRef = doc(db, 'sidebarConfigs', 'business');
      const oldSnap = await getDoc(oldBusinessRef);
      if (oldSnap.exists()) {
        const oldData = oldSnap.data();
        return oldData.menuConfig || oldData.menuItems;
      }
    } catch (err) {
      console.warn(`Legacy fallback error:`, err);
    }
    return null;
  }

  // Get default sectoral sidebar configuration
  getDefaultSectoralSidebar(sector) {
    const defaultConfigs = {
      'food-beverage': [
        // ✅ CANONICAL CONFIG — Plana göre (PLAN_MODULE_SIDEBAR_ARCHITECTURE_2025.md §6.2)
        // Paket ne ise modülleri aktif, geri kalanlar filterVisibleItems() tarafından gizlenir.
        {
          id: 'restaurant_dashboard',
          type: 'single',
          label: 'Anasayfa',
          path: '/sectors/food-beverage/dashboard',
          icon: 'HomeIcon',
          color: 'from-indigo-500 to-purple-400',
          visible: true,
          moduleId: 'core_access',
          order: 1
        },
        {
          id: 'pos',
          type: 'single',
          label: 'POS & Satış',
          path: '/sectors/food-beverage/pos/dashboard',
          icon: 'ShoppingCartIcon',
          color: 'from-orange-500 to-amber-400',
          visible: true,
          moduleId: 'pos_basic',
          order: 2
        },
        {
          id: 'order-tracking',
          type: 'single',
          label: 'Sipariş Takip',
          path: '/sectors/food-beverage/operations/order-tracking',
          icon: 'ClipboardDocumentListIcon',
          color: 'from-blue-500 to-cyan-400',
          visible: true,
          moduleId: 'pos_basic',
          order: 3
        },
        {
          id: 'operations',
          type: 'single',
          label: 'Operasyon & Mutfak',
          path: '/sectors/food-beverage/operations',
          icon: 'BoltIcon',
          color: 'from-red-500 to-orange-400',
          visible: true,
          moduleId: 'stock_basic',
          order: 4
        },
        {
          id: 'campaigns',
          type: 'single',
          label: 'Pazarlama & CRM',
          path: '/sectors/food-beverage/campaigns',
          icon: 'MegaphoneIcon',
          color: 'from-pink-500 to-rose-400',
          visible: true,
          moduleId: ['crm', 'qr_menu'],
          order: 4
        },
        {
          id: 'whatsapp',
          type: 'single',
          label: 'WhatsApp',
          path: '/business/whatsapp',
          icon: 'ChatBubbleLeftRightIcon',
          color: 'from-emerald-500 to-green-400',
          visible: true,
          moduleId: 'core_access',
          order: 5
        },
        {
          id: 'finance',
          type: 'single',
          label: 'Finansal Yönetim',
          path: '/sectors/food-beverage/finance',
          icon: 'BanknotesIcon',
          color: 'from-emerald-500 to-teal-400',
          visible: true,
          moduleId: 'accounting',
          order: 6
        },
        {
          id: 'reports',
          type: 'single',
          label: 'Raporlar & Analitik',
          path: '/sectors/food-beverage/reports/dashboard',
          icon: 'ChartBarIcon',
          color: 'from-purple-500 to-indigo-400',
          visible: true,
          moduleId: 'reporting_basic',
          order: 7
        },
        {
          id: 'hr_management',
          type: 'single',
          label: 'Personel Yönetimi',
          path: '/sectors/food-beverage/hr/staff-management',
          icon: 'UsersIcon',
          color: 'from-blue-600 to-indigo-400',
          visible: true,
          moduleId: 'hr_management',
          order: 8
        },
        // ── 8. Ayarlar ──────────────────────────────────────────────────
        {
          id: 'settings',
          type: 'single',
          label: 'Ayarlar',
          path: '/settings',
          icon: 'SettingsIcon',
          color: 'from-gray-500 to-slate-400',
          visible: true,
          order: 9
        }
      ],
      'hospitality': [
        {
          id: 'hospitality_dashboard',
          type: 'single',
          label: 'Genel Bakış',
          path: '/sectors/service/hospitality/dashboard',
          icon: 'HomeIcon',
          color: 'from-amber-500 to-yellow-400',
          visible: true,
          moduleId: 'core_access',
          order: 1
        },
        {
          id: 'rooms',
          type: 'single',
          label: 'Oda Yönetimi',
          path: '/sectors/service/hospitality/rooms',
          icon: 'BuildingOfficeIcon',
          color: 'from-blue-500 to-cyan-400',
          visible: true,
          moduleId: 'room_management',
          order: 2
        },
        {
          id: 'reservations',
          type: 'single',
          label: 'Rezervasyonlar',
          path: '/sectors/service/hospitality/reservations',
          icon: 'CalendarDaysIcon',
          color: 'from-green-500 to-emerald-400',
          visible: true,
          moduleId: 'reservation_management',
          order: 3
        },
        {
          id: 'guests',
          type: 'single',
          label: 'Misafir Yönetimi',
          path: '/sectors/service/hospitality/guests',
          icon: 'UsersIcon',
          color: 'from-indigo-500 to-purple-400',
          visible: true,
          moduleId: 'crm',
          order: 4
        },
        {
          id: 'campaigns',
          type: 'single',
          label: 'Pazarlama & CRM',
          path: '/sectors/service/hospitality/campaigns',
          icon: 'MegaphoneIcon',
          color: 'from-pink-500 to-rose-400',
          visible: true,
          moduleId: 'crm',
          order: 5
        },
        {
          id: 'finance',
          type: 'single',
          label: 'Finansal Yönetim',
          path: '/sectors/service/hospitality/finance',
          icon: 'BanknotesIcon',
          color: 'from-emerald-500 to-teal-400',
          visible: true,
          moduleId: 'accounting',
          order: 6
        },
        {
          id: 'reports',
          type: 'single',
          label: 'Raporlar & Analitik',
          path: '/sectors/service/hospitality/reports/dashboard',
          icon: 'ChartBarIcon',
          color: 'from-purple-500 to-indigo-400',
          visible: true,
          moduleId: 'reporting_basic',
          order: 7
        },
        {
          id: 'hr_management',
          type: 'single',
          label: 'Personel Yönetimi',
          path: '/sectors/service/hospitality/hr/staff-management',
          icon: 'BriefcaseIcon',
          color: 'from-blue-600 to-indigo-400',
          visible: true,
          moduleId: 'hr_management',
          order: 8
        },
        {
          id: 'settings',
          type: 'single',
          label: 'Otel Ayarları',
          path: '/settings',
          icon: 'Cog6ToothIcon',
          color: 'from-gray-500 to-slate-400',
          visible: true,
          order: 9
        }
      ],
      'service': [
        {
          id: 'dashboard',
          label: 'Dashboard',
          path: '/sectors/service/dashboard',
          icon: 'HomeIcon',
          color: 'from-purple-500 to-indigo-400',
          visible: true,
          moduleId: 'core_access'
        },
        {
          id: 'appointments',
          label: 'Randevu Yönetimi',
          icon: 'CalendarDaysIcon',
          color: 'from-blue-500 to-cyan-400',
          visible: true,
          moduleId: 'pos_basic', // Using pos_basic as base for service appointments for now or create specific module
          children: [
            {
              id: 'appointment-calendar',
              label: 'Randevu Takvimi',
              path: '/sectors/service/pages/appointments/AppointmentCalendar',
              visible: true
            },
            {
              id: 'client-management',
              label: 'Müşteri Yönetimi',
              path: '/sectors/service/pages/appointments/ClientManagement',
              visible: true,
              moduleId: 'crm'
            },
            {
              id: 'online-booking',
              label: 'Online Rezervasyon',
              path: '/sectors/service/pages/booking/OnlineAppointmentBooking',
              visible: true,
              moduleId: 'ecommerce'
            }
          ]
        },
        {
          id: 'services',
          label: 'Hizmetler',
          icon: 'WrenchScrewdriverIcon',
          color: 'from-green-500 to-emerald-400',
          visible: true,
          moduleId: 'stock_basic',
          children: [
            {
              id: 'service-catalog',
              label: 'Hizmet Kataloğu',
              path: '/sectors/service/pages/services/ServiceCatalog',
              visible: true
            },
            {
              id: 'pricing',
              label: 'Fiyat Listesi',
              path: '/sectors/service/pages/services/PriceList',
              visible: true
            }
          ]
        },
        {
          id: 'staff',
          label: 'Personel Yönetimi',
          icon: 'UserGroupIcon',
          color: 'from-orange-500 to-red-400',
          visible: true,
          moduleId: 'hr_management',
          children: [
            {
              id: 'staff-schedule',
              label: 'Çalışan Programı',
              path: '/sectors/service/pages/staff/StaffSchedule',
              visible: true
            },
            {
              id: 'staff-performance',
              label: 'Performans Takibi',
              path: '/sectors/service/pages/staff/Performance',
              visible: true
            }
          ]
        },
        {
          id: 'reports',
          label: 'Raporlar',
          icon: 'ChartBarIcon',
          color: 'from-yellow-500 to-orange-400',
          visible: true,
          moduleId: 'reporting_basic',
          children: [
            {
              id: 'service-reports',
              label: 'Hizmet Raporları',
              path: '/sectors/service/pages/analytics/ServiceReports',
              visible: true
            },
            {
              id: 'revenue-reports',
              label: 'Gelir Raporları',
              path: '/sectors/service/pages/analytics/RevenueReports',
              visible: true
            },
            {
              id: 'domain-management',
              label: 'Domain Yönetimi',
              path: '/settings/domains',
              visible: true,
              icon: 'GlobeAltIcon'
            }
          ]
        }
      ],
      'retail': [
        {
          id: 'dashboard',
          label: 'Dashboard',
          path: '/sectors/retail/dashboard',
          icon: 'HomeIcon',
          color: 'from-blue-500 to-indigo-400',
          visible: true,
          moduleId: 'core_access'
        },
        {
          id: 'pos',
          label: 'POS Satış',
          icon: 'BuildingStorefrontIcon',
          color: 'from-green-500 to-emerald-400',
          visible: true,
          moduleId: 'pos_basic',
          children: [
            {
              id: 'retail-pos',
              label: 'Mağaza Satışı',
              path: '/sectors/retail/pages/pos/RetailPOS',
              visible: true
            },
            {
              id: 'quick-checkout',
              label: 'Hızlı Ödeme',
              path: '/sectors/retail/pages/pos/QuickCheckout',
              visible: true
            }
          ]
        },
        {
          id: 'products',
          label: 'Ürün Yönetimi',
          icon: 'CubeIcon',
          color: 'from-purple-500 to-indigo-400',
          visible: true,
          moduleId: 'stock_basic',
          children: [
            {
              id: 'product-management',
              label: 'Ürün Kataloğu',
              path: '/sectors/retail/pages/products/ProductManagement',
              visible: true
            },
            {
              id: 'categories',
              label: 'Kategoriler',
              path: '/sectors/retail/pages/products/Categories',
              visible: true
            },
            {
              id: 'inventory',
              label: 'Stok Yönetimi',
              path: '/sectors/retail/pages/inventory/InventoryManagement',
              visible: true,
              moduleId: 'stock_pro'
            }
          ]
        },
        {
          id: 'orders',
          label: 'Sipariş Yönetimi',
          icon: 'ClipboardDocumentListIcon',
          color: 'from-orange-500 to-red-400',
          visible: true,
          moduleId: 'pos_basic',
          children: [
            {
              id: 'order-management',
              label: 'Sipariş Listesi',
              path: '/sectors/retail/pages/orders/OrderManagement',
              visible: true
            },
            {
              id: 'online-orders',
              label: 'Online Siparişler',
              path: '/sectors/retail/pages/orders/OnlineOrders',
              visible: true,
              moduleId: 'ecommerce'
            }
          ]
        },
        {
          id: 'ecommerce',
          label: 'E-Ticaret',
          icon: 'TruckIcon',
          color: 'from-cyan-500 to-blue-400',
          visible: true,
          moduleId: 'ecommerce',
          children: [
            {
              id: 'ecommerce-management',
              label: 'Online Mağaza',
              path: '/sectors/retail/pages/ecommerce/EcommerceManagement',
              visible: true
            },
            {
              id: 'marketplace-integration',
              label: 'Pazaryeri Entegrasyonu',
              path: '/sectors/retail/pages/ecommerce/MarketplaceIntegration',
              visible: true,
              moduleId: 'marketplace_integrations'
            }
          ]
        },
        {
          id: 'warehouse',
          label: 'Depo Yönetimi',
          icon: 'ArchiveBoxIcon',
          color: 'from-indigo-500 to-purple-400',
          visible: true,
          moduleId: 'stock_pro',
          children: [
            {
              id: 'warehouse-management',
              label: 'Depo İşlemleri',
              path: '/sectors/retail/pages/warehouse/WarehouseManagement',
              visible: true
            },
            {
              id: 'stock-transfer',
              label: 'Stok Transferi',
              path: '/sectors/retail/pages/warehouse/StockTransfer',
              visible: true
            }
          ]
        },
        {
          id: 'settings_group',
          label: 'Ayarlar',
          icon: 'Cog6ToothIcon',
          color: 'from-gray-500 to-slate-400',
          visible: true,
          moduleId: 'core_access',
          type: 'group',
          order: 99,
          children: [
            {
              id: 'domain-management',
              label: 'Domain Yönetimi',
              path: '/settings/domains',
              visible: true,
              icon: 'GlobeAltIcon'
            }
          ]
        }
      ]
    };

    // ✅ Tüm sektörler için canonical defaultConfigs kullan
    const sectorKey = sector.replace('_', '-'); // food_beverage → food-beverage
    return defaultConfigs[sector] || defaultConfigs[sectorKey] || [];
  }

  // Check if business has access to a module
  hasModuleAccess(business, moduleId) {
    if (!moduleId) return true; // Modül gerektirmiyor → herkes erişebilir
    if (!business) return false;

    // Handle array of module IDs
    if (Array.isArray(moduleId)) {
      return moduleId.some(id => this.hasModuleAccess(business, id));
    }

    // 1. business.modules (BusinessSidebar'dan gelen activeModuleIds listesi)
    if (business.modules && business.modules.includes(moduleId)) return true;

    // 2. add_ons
    if (business.add_ons && business.add_ons.includes(moduleId)) return true;

    // 3. Subscription altında active_modules
    if (business.subscription?.active_modules && business.subscription.active_modules.includes(moduleId)) return true;

    // 4. Full paket bypass: tüm modüllere erişim
    const pkgName = (business.subscription?.package_name || business.packageName || '').toLowerCase();
    if (pkgName.includes('full') || pkgName.includes('enterprise') || pkgName.includes('tam')) {
      return true;
    }

    return false;
  }

  // Menü öğelerini visibility ve modül erişimine göre filtrele
  filterVisibleItems(menuItems, business) {
    if (!menuItems || !Array.isArray(menuItems)) return [];

    return menuItems
      .filter(item => {
        // 1. Temel görünürlük kontrolü
        if (item.visible === false) return false;

        // 2. Grup mödül kontrolü: Grubun kendisine tanımlı modül yoksa tümü gizlenir
        if (item.moduleId && item.type === 'group') {
          const hasAccess = this.hasModuleAccess(business, item.moduleId);
          if (!hasAccess) return false;
        }

        // 3. Tek öğe modül kontrolü
        if (item.moduleId && item.type !== 'group') {
          const hasAccess = this.hasModuleAccess(business, item.moduleId);
          if (!hasAccess) return false;
        }

        return true;
      })
      .map(item => {
        if (!item.children) return item;
        // Çocukları da filtrele
        const filteredChildren = this.filterVisibleItems(item.children, business);
        return { ...item, children: filteredChildren };
      })
      // Alt menüsü tamamen boşalan grupları gizle
      .filter(item => {
        if (item.type === 'group') {
          return item.children && item.children.length > 0;
        }
        return true;
      });
  }

  // Clear cache for a specific sector
  clearCache(sector) {
    const cacheKey = this.getCacheKey(sector);
    delete this.cache[cacheKey];
  }

  // Clear all cache
  clearAllCache() {
    this.cache = {};
  }

  // Get current sector from localStorage
  getCurrentSector() {
    const businessSector = localStorage.getItem('businessSector');
    const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || '{}');

    // Priority: businessSector from localStorage, then selectedBusiness sector, then default
    if (businessSector && businessSector !== 'null') {
      return businessSector;
    }

    if (selectedBusiness?.sector) {
      // Handle Firebase sector ID to sector name mapping
      const sectorMap = {
        '9Hi9XsqIEp0k5cLCtSaM': 'food-beverage',
        'jC8leOrZ5D9jycx1fknk': 'retail',
        'm9q78OoqRzawqJhnG4yW': 'service',
        'VD0Ndssw0KD34ZoHnRTV': 'food-beverage',
        'GaGHwMSCFa3wFOQyhdtP': 'retail',
        '7hXb9mxWP8e1dtP2suvi': (selectedBusiness.category === 'hospitality' || selectedBusiness.business_type === 'hospitality' || (selectedBusiness.name || '').toLowerCase().includes('otel')) ? 'hospitality' : 'service'
      };

      const mappedSector = sectorMap[selectedBusiness.sector] || selectedBusiness.sector;
      return mappedSector;
    }

    return 'food-beverage'; // default
  }

  // Load sectoral sidebar for current business - SIMPLIFIED
  async loadCurrentSectoralSidebar() {
    const currentSector = this.getCurrentSector();
    const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || '{}');

    console.log(`🔄 SIMPLE: Loading current sectoral sidebar for: ${currentSector}`);
    console.log(`🔐 Business Module Check for: ${selectedBusiness.name}`);

    // Always get fresh data (no cache)
    const sidebarItems = await this.getSectoralSidebar(currentSector);

    // Filter by both visibility and module access
    const visibleItems = this.filterVisibleItems(sidebarItems, selectedBusiness);

    console.log(`📋 SIMPLE: Returning ${visibleItems.length} visible items out of ${sidebarItems.length} total`);
    return visibleItems;
  }
}

// Export singleton instance
export const sectoralSidebarService = new SectoralSidebarService();
export default sectoralSidebarService;